import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Route, Settings2 } from "lucide-react";
import { Loading, Routes as RoutesAPI } from "../lib/api";
import { plural } from "../components/loading/cargoSlots";
import { haptic, HAPTIC } from "../lib/haptics";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../components/ConfirmProvider";
import { LoadingItemForm } from "../components/loading/LoadingItemForm";
import { LoadingItemList } from "../components/loading/LoadingItemList";
import { RoutePreview } from "../components/loading/RoutePreview";
import { RouteSetup } from "../components/loading/RouteSetup";
import { MyTemplateDialog } from "../components/loading/MyTemplateDialog";
import { PageSkeleton } from "../components/PageSkeleton";

const errorText = (err, fallback) => (typeof err.response?.data?.detail === "string" ? err.response.data.detail : fallback);

export default function LoadingPage() {
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [templateDialog, setTemplateDialog] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("setup");

  const loadTemplates = useCallback(() => RoutesAPI.listTemplates().then(setTemplates), []);
  useEffect(() => {
    let alive = true;
    Promise.all([Loading.getDraft(), loadTemplates()]).then(([d]) => { if (alive) { setDraft(d); setStep(d.items.length ? "entry" : "setup"); } }).catch(() => toast.error("Nie udało się wczytać załadunku"));
    return () => { alive = false; };
  }, [loadTemplates]);

  const save = async (next) => {
    setDraft(next); setPreview(null);
    try { await Loading.saveDraft(next); } catch (err) { toast.error(errorText(err, "Nie udało się zapisać zmian")); }
  };
  const template = templates.find((t) => t.id === draft?.template_id);
  const streets = useMemo(() => [...new Set((template?.rules || []).map((r) => r.street).filter(Boolean))], [template]);

  const submitItem = (item) => {
    const items = editing ? draft.items.map((it) => (it.id === editing.id ? { ...item, id: editing.id } : it)) : [...draft.items, { ...item, id: crypto.randomUUID().replace(/-/g, "") }];
    setEditing(null);
    haptic(HAPTIC.add);
    save({ ...draft, items });
  };
  const addBag = (color) => {
    const bags = draft.bags || [];
    save({ ...draft, bags: [...bags, { id: crypto.randomUUID().slice(0, 8), number: Math.max(0, ...bags.map((b) => b.number)) + 1, color }] });
  };
  const removeBag = async (bag) => {
    const code = `bag:${bag.id}`;
    const used = draft.items.filter((it) => it.cargo_slot === code).length;
    if (used && !(await confirm(`W tej torbie jest ${plural(used, "paczka", "paczki", "paczek")}. Usunąć torbę? Paczki stracą miejsce na pace.`, { tone: "danger", confirmLabel: "Usuń torbę" }))) return;
    save({ ...draft, bags: draft.bags.filter((b) => b.id !== bag.id), items: draft.items.map((it) => (it.cargo_slot === code ? { ...it, cargo_slot: "" } : it)) });
  };
  const removeItem = async (item) => {
    if (await confirm(`Usunąć paczkę „${item.address_text}”?`, { tone: "danger", confirmLabel: "Usuń" })) save({ ...draft, items: draft.items.filter((it) => it.id !== item.id) });
  };
  const selectTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    save({ ...draft, template_id: id, city: t?.default_city || draft.city });
  };
  const onTemplateSaved = async (saved) => { setTemplateDialog(null); await loadTemplates(); save({ ...draft, template_id: saved.id, city: saved.default_city || draft.city }); };
  const copyTemplate = async () => {
    try { const copy = await RoutesAPI.duplicateTemplate(template.id, true); toast.success(`Utworzono: ${copy.name}`); await loadTemplates(); setTemplateDialog(copy); } catch (err) { toast.error(errorText(err, "Nie udało się skopiować")); }
  };
  const deleteTemplate = async () => {
    if (!(await confirm(`Usunąć szablon „${template.name}”?`, { tone: "danger", confirmLabel: "Usuń szablon" }))) return;
    try { await RoutesAPI.removeTemplate(template.id); await save({ ...draft, template_id: "" }); loadTemplates(); } catch (err) { toast.error(errorText(err, "Nie udało się usunąć")); }
  };
  const arrange = async () => {
    setBusy(true);
    try { await Loading.saveDraft(draft); setPreview((await Loading.preview()).stops); } catch (err) { toast.error(errorText(err, "Nie udało się ułożyć trasy")); } finally { setBusy(false); }
  };
  const dispatch = async () => {
    setBusy(true);
    try {
      const r = await Loading.dispatch();
      haptic(HAPTIC.send);
      toast.success(`Trasa wysłana do Zleceń · ${r.stops_count} przystanków`);
      navigate("/zlecenia?view=swipe");
    } catch (err) { toast.error(errorText(err, "Nie udało się wysłać trasy")); setBusy(false); }
  };

  if (!draft) return <div data-testid="loading-page-loader"><PageSkeleton label="Wczytuję załadunek…" rows={3} /></div>;
  const templateBarProps = { templates, userId: user?.user_id, value: draft.template_id, onSelect: selectTemplate, onNew: () => setTemplateDialog({}), onEdit: () => setTemplateDialog(template), onCopy: copyTemplate, onDelete: deleteTemplate };
  const count = plural(draft.items.length, "paczka", "paczki", "paczek");
  return (
    <div className="-mt-5 md:mt-0 p-0 md:p-6 space-y-3 max-w-2xl mx-auto" data-testid="loading-page">
      {step === "setup" ? <RouteSetup draft={draft} setDraft={setDraft} save={save} templateBarProps={templateBarProps} onNext={() => { save(draft); setStep("entry"); }} /> : <>
        <button type="button" data-testid="loading-summary-bar" onClick={() => { setEditing(null); setStep("setup"); }} className="w-full flex items-center gap-3 rounded-2xl bg-zinc-900 text-white px-3 py-2 text-left hover:bg-zinc-800 transition-colors">
          <div className="min-w-0 flex-1">
            <p data-testid="loading-summary-name" className="text-sm font-black uppercase truncate">{draft.name || "Załadunek"}</p>
            <p data-testid="loading-summary-template" className="text-[11px] text-zinc-400 truncate">{`${template?.name || "Bez szablonu"} · ${draft.city}`}</p>
          </div>
          <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-orange-400"><Settings2 className="w-3.5 h-3.5" />Zmień</span>
        </button>
        <LoadingItemForm initial={editing} streets={streets} bags={draft.bags || []} onAddBag={addBag} onRemoveBag={removeBag} onSubmit={submitItem} onCancel={editing ? () => setEditing(null) : null} />
        <LoadingItemList items={draft.items} newestFirst bags={draft.bags || []} editingId={editing?.id} onEdit={(it) => { setEditing(it); window.scrollTo({ top: 0, behavior: "smooth" }); }} onRemove={removeItem} />
        <div className="sticky bottom-[72px] md:bottom-2 z-20 flex items-center gap-2 rounded-2xl bg-white/90 backdrop-blur-md ring-1 ring-zinc-200 shadow-lg p-2">
          <span data-testid="loading-items-count" className="px-3 py-2 rounded-xl bg-zinc-100 text-xs font-black whitespace-nowrap">{count}</span>
          <button type="button" data-testid="loading-arrange-btn" disabled={busy || !draft.items.length} onClick={arrange} className="flex-1 h-11 inline-flex items-center justify-center gap-2 rounded-xl bg-zinc-900 text-white text-sm font-black uppercase tracking-wider hover:bg-orange-600 transition-colors disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Route className="w-4 h-4" />}Ułóż trasę
          </button>
        </div>
      </>}
      {preview && createPortal(
        <div data-testid="loading-preview-sheet" className="fixed inset-0 z-[1200] bg-black/60 flex items-end sm:items-center justify-center p-2" onClick={(e) => { if (e.target === e.currentTarget) setPreview(null); }}>
          <div className="w-full max-w-2xl max-h-[92dvh] overflow-y-auto rounded-2xl"><RoutePreview stops={preview} busy={busy} onDispatch={dispatch} onClose={() => setPreview(null)} /></div>
        </div>, document.body
      )}
      {templateDialog && <MyTemplateDialog template={templateDialog} onClose={() => setTemplateDialog(null)} onSaved={onTemplateSaved} />}
    </div>
  );
}
